import * as geojson from 'geojson';
import React from 'react';

import {recordEvent} from 'app/tools/Analytics';
import {useStateWithDeps} from 'app/utils/hookUtils';

import {GridContext, GridContextValue} from './GridContext';
import {useGridGeometry} from './gridHooks';
import {CellId, Size} from './types';
import {clearGridState, readGridState, writeGridState} from './utils';

interface Props {
  projectId: string;
  featureId: number | null;
  featureGeometry: geojson.Geometry | null;
  children: React.ReactNode;
}

/**
 * Owns all grid-related state and exposes it via {@link useGrid}. Sibling
 * grid components consume this context rather than holding state themselves.
 *
 * `useStateWithDeps([featureId])` resets ui state when the active property
 * changes, so each property gets a fresh grid configuration.
 *
 * `isEnabled` is intentionally session-only (not persisted): a refresh starts
 * with the tool off, even if the user left it open last time. `gridSize` and
 * `reviewedCellIds` are persisted per (projectId, featureId) so reopening the
 * tool returns straight to in-progress review.
 */
const GridProvider: React.FunctionComponent<Props> = ({
  projectId,
  featureId,
  featureGeometry,
  children,
}) => {
  const savedGrid = React.useMemo(
    () => readGridState(projectId, featureId),
    [projectId, featureId]
  );

  const [isEnabled, setIsEnabled] = useStateWithDeps(false, [featureId]);
  const [gridSize, setGridSize] = useStateWithDeps<Size | null>(savedGrid?.gridSize ?? null, [
    featureId,
  ]);
  const [reviewedCellIds, setReviewedCellIds] = useStateWithDeps<Set<CellId>>(
    React.useMemo(() => new Set<CellId>(savedGrid?.reviewedCellIds ?? []), [savedGrid]),
    [featureId]
  );
  // Picker is open until the user confirms a size. Starts open only when
  // there's no persisted size for this property.
  const [isPickingGridSize, setIsPicking] = useStateWithDeps(savedGrid?.gridSize == null, [featureId]);

  const {spec, featureCollection, existingCellIds} = useGridGeometry(featureGeometry, gridSize);
  const [activeCellId, setActiveCellId] = useStateWithDeps<CellId | null>(null, [spec]);

  const toggleCellReviewed = React.useCallback(
    (id: CellId) => {
      const next = new Set(reviewedCellIds);
      next.has(id) ? next.delete(id) : next.add(id);
      setReviewedCellIds(next);
      writeGridState(projectId, featureId, {gridSize, reviewedCellIds: next});
    },
    [reviewedCellIds, setReviewedCellIds, featureId, projectId, gridSize]
  );

  // Picker clicks update gridSize directly (live preview on the map) without
  // touching localStorage. This is the only place that commits and persists.
  const confirmGridSize = React.useCallback(() => {
    if (gridSize === null) return;
    writeGridState(projectId, featureId, {gridSize, reviewedCellIds});
    setIsPicking(false);
    recordEvent('Created review grid', {
      ...(featureId != null && {featureId}),
    });
  }, [gridSize, reviewedCellIds, projectId, featureId, setIsPicking]);

  const resetGrid = React.useCallback(() => {
    setGridSize(null);
    setIsPicking(true);
    setReviewedCellIds(new Set());
    setActiveCellId(null);
    clearGridState(projectId, featureId);
  }, [setGridSize, setIsPicking, setReviewedCellIds, setActiveCellId, projectId, featureId]);

  const value = React.useMemo<GridContextValue>(
    () => ({
      projectId,
      featureId,
      featureGeometry,
      isEnabled,
      setIsEnabled,
      gridSize,
      setGridSize,
      isPickingGridSize,
      confirmGridSize,
      activeCellId,
      setActiveCellId,
      reviewedCellIds,
      toggleCellReviewed,
      resetGrid,
      spec,
      featureCollection,
      existingCellIds,
    }),
    [
      projectId,
      featureId,
      featureGeometry,
      isEnabled,
      setIsEnabled,
      gridSize,
      setGridSize,
      isPickingGridSize,
      confirmGridSize,
      activeCellId,
      setActiveCellId,
      reviewedCellIds,
      toggleCellReviewed,
      resetGrid,
      spec,
      featureCollection,
      existingCellIds,
    ]
  );

  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
};

export default GridProvider;
